import { useEffect, useLayoutEffect, useRef } from 'react';

import { useFormikForm } from '#/common/hooks';
import { ENTER } from '#/utils/constants';
import { chatbotInputSchema } from '#/utils/ValidationSchema';

export function useChatbotPreview(
  updateMessages,
  resetFaqId,
  contentRef,
  isFieldSubmit,
  setIsFieldSubmit,
  setUploadedFiles,
  isStreaming,
  isSending,
  inputRef
) {
  const initialValues = { inputField: '' };
  const wasStreamingRef = useRef(false);

  const onSubmit = values => {
    if (!values.inputField.trim()) return;
    updateMessages(values.inputField);
    resetForm();
    resetFaqId('');
    setUploadedFiles({});
  };

  const {
    values: { inputField },
    handleChange,
    submitForm,
    resetForm,
  } = useFormikForm(initialValues, onSubmit, chatbotInputSchema);

  const handleEnterKeyPress = e => {
    if (e.key === ENTER && inputField.trim()) {
      e.preventDefault();
      submitForm();
    }
  };

  const scrollToBottom = () => {
    if (contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  };

  useLayoutEffect(() => {
    scrollToBottom();
  }, []);

  useEffect(() => {
    if (isStreaming) {
      wasStreamingRef.current = true;
    }

    if (wasStreamingRef.current && !isSending) {
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
      wasStreamingRef.current = false;
    }
  }, [isStreaming, isSending, inputRef]);

  const handlePreviewToggle = () => {
    resetForm();
    resetFaqId('');
    setIsFieldSubmit(!isFieldSubmit);
    setUploadedFiles({});
    scrollToBottom();
  };

  useEffect(() => {
    const handleShowPreview = event => {
      const { action, value } = event.data || {};
      if (action === 'showWidgetPreviewChange') {
        if (value) {
          handlePreviewToggle();
        }
      }
    };

    window.addEventListener('message', handleShowPreview);
    return () => window.removeEventListener('message', handleShowPreview);
  }, []);

  return {
    handlePreviewToggle,
    inputField,
    handleChange,
    handleEnterKeyPress,
    submitForm,
  };
}
